import {
  ActionShare,
  ActionThumbUp,
  ContentsPlayCircle,
  InfoCalendar,
  NavigationMyPage,
} from '@teamsparta/stack-icons';
import { vars } from '@teamsparta/stack-tokens';
import { useSetAtom } from 'jotai';
import Image from 'next/legacy/image';
import { useRouter } from 'next/router';

import type { WebinarDto } from '@/api/webinar';
import { webinarToastAtom } from '@/components/webinar/WebinarToast/WebinarToast';
import {
  useUpdateWebinarLike,
  useWebinarUser,
} from '@/hooks/react-query/webinar';
import {
  getDateWithSymbol,
  getHourWithSymbol,
} from '@/lib/utils/businessLogics/utils/date';
import { sendLog } from '@/lib/utils/send-log';
import { useLoginModal } from '@/store/login';

import CourseBanner from '../CourseBanner';
import * as S from './WebinarVideoSection.style';
import { SocialButton } from './WebinarVideoSection.style';

type WebinarVideoSectionProps = {
  webinar: WebinarDto;
  onlineUserId: string;
};
const WebinarVideoSection = ({
  webinar,
  onlineUserId,
}: WebinarVideoSectionProps) => {
  const router = useRouter();
  const setToast = useSetAtom(webinarToastAtom);
  const { video, lecturer, metadata, status } = webinar;
  const { webinarUser, isLoading } = useWebinarUser(webinar._id, onlineUserId);
  const { mutate } = useUpdateWebinarLike();
  const loginModal = useLoginModal();
  const hour = Math.floor(video.time / 3600);
  const minute = Math.floor((video.time % 3600) / 60);
  const broadcastedAt = new Date(video.broadcastedAt);
  broadcastedAt.setHours(broadcastedAt.getHours() - 9);
  const onClickLike = () => {
    if (!onlineUserId) {
      return pushToLoginPage();
    }
    mutate({
      webinarId: webinar._id,
      onlineUserId,
      targetLike: !webinarUser.like,
    });
  };

  function pushToLoginPage() {
    loginModal.open(router.asPath);
  }

  async function copyCurrentUrl() {
    const currentUrl = window.document.location.href;
    try {
      await navigator.clipboard.writeText(currentUrl);
      sendLog('hh_webinarDetailPage_shareButton_click', {
        webinar_name: webinar.video.title,
      });
      setToast('링크가 복사되었습니다!');
    } catch (err) {
      alert('링크 복사에 실패하였습니다.');
    }
  }

  function getStrByCareer(career: string): string {
    if (career === 'developer') {
      return '개발자';
    }
    if (career === 'ceo') {
      return '대표';
    }
    if (career === 'data-science-leader') {
      return '데이터사이언스팀 리더';
    }
    return '';
  }

  return (
    <>
      <S.Section>
        {status === 'upcoming' ? (
          <S.ThumbnailWrapper>
            <Image
              src={`${process.env.NEXT_PUBLIC_CDN_BASE_URL}/${webinar.info.thumbnail}`}
              layout='fill'
              objectFit='contain'
              alt='thumbnail'
            />
          </S.ThumbnailWrapper>
        ) : (
          <S.VideoWrapper allBordersRadius={!!onlineUserId}>
            {onlineUserId ? (
              <iframe
                width='100%'
                height='100%'
                src={
                  webinar.video.url +
                  `${webinar.video.url.includes('?') ? '&' : '?'}autoplay=1`
                }
                title='YouTube video player'
                frameBorder='0'
                allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                allowFullScreen
              />
            ) : (
              <video
                src={`${process.env.NEXT_PUBLIC_CDN_BASE_URL}/${webinar.video.previewUrl}`}
                controls
                autoPlay
                playsInline
                width='100%'
              />
            )}
            {!onlineUserId && (
              <S.PreviewMessageAndLoginButton>
                <S.PreviewMessage>
                  <S.PreviewMessageBold>
                    1분 미리보기 영상입니다.
                  </S.PreviewMessageBold>
                  로그인 후 풀버전을 시청하세요.
                </S.PreviewMessage>
                <S.LoginButton
                  onClick={() => {
                    sendLog('hh_webinarDetailPage_loginButton_click', {});
                    pushToLoginPage();
                  }}
                >
                  로그인
                </S.LoginButton>
              </S.PreviewMessageAndLoginButton>
            )}
          </S.VideoWrapper>
        )}

        <S.InfoContainer>
          <S.VideoInfoContainer>
            <S.Title>{video.title}</S.Title>
            <S.VideoInfo>
              <S.DetailInfo>
                <NavigationMyPage
                  size={20}
                  color={vars.neutral[50]}
                  variant='line'
                />
                <p>
                  {lecturer.name} {getStrByCareer(webinar.lecturer.career)}
                </p>
              </S.DetailInfo>
              {status === 'done' && (
                <S.DetailInfo>
                  <ContentsPlayCircle
                    size={20}
                    color={vars.neutral[50]}
                    variant='line'
                  />
                  <p>
                    {hour > 0 && hour + '시간'} {minute + '분'}
                  </p>
                </S.DetailInfo>
              )}
              <S.DetailInfo>
                <InfoCalendar
                  size={20}
                  color={vars.neutral[50]}
                  variant='line'
                />
                <p>
                  {getDateWithSymbol(broadcastedAt, '.')}&nbsp;
                  {status !== 'done' && getHourWithSymbol(broadcastedAt, ':')}
                </p>
              </S.DetailInfo>
            </S.VideoInfo>
          </S.VideoInfoContainer>
          {!isLoading && (
            <S.SocialContainer>
              {webinar.status !== 'upcoming' && (
                <SocialButton
                  className={webinarUser?.like ? 'active' : ''}
                  onClick={onClickLike}
                >
                  {webinarUser?.like ? (
                    <ActionThumbUp
                      size={16}
                      color={vars.neutral[0]}
                      variant='fill'
                    />
                  ) : (
                    <ActionThumbUp
                      size={16}
                      color={vars.neutral[0]}
                      variant='line'
                    />
                  )}
                  {metadata.likes}
                </SocialButton>
              )}
              <SocialButton onClick={copyCurrentUrl}>
                <ActionShare size={16} color={vars.neutral[0]} />
                공유
              </SocialButton>
            </S.SocialContainer>
          )}
          {webinar.status === 'done' && (
            <CourseBanner className='mobile' course={metadata.course} />
          )}
        </S.InfoContainer>
      </S.Section>
    </>
  );
};
export default WebinarVideoSection;
